import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Receipt, Loader2, Calendar as CalendarIcon, ArrowRight, CalendarDays, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { format, addDays, parseISO, differenceInDays, startOfDay, endOfDay } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillsPage() {
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteAllStep, setDeleteAllStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [formData, setFormData] = useState({
    bill_date: format(new Date(), 'yyyy-MM-dd'),
    supplier_id: '',
    description: '',
    amount: '',
    extra_days: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [billsRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/bills`),
        axios.get(`${API}/suppliers`)
      ]);
      setBills(billsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (bill = null) => {
    if (bill) {
      setEditingBill(bill);
      setFormData({
        bill_date: bill.bill_date,
        supplier_id: bill.supplier_id,
        description: bill.description || '',
        amount: bill.amount.toString(),
        extra_days: bill.extra_days
      });
    } else {
      setEditingBill(null);
      setFormData({
        bill_date: format(new Date(), 'yyyy-MM-dd'),
        supplier_id: suppliers[0]?.id || '',
        description: '',
        amount: '',
        extra_days: 0
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        extra_days: parseInt(formData.extra_days) || 0
      };

      if (editingBill) {
        await axios.put(`${API}/bills/${editingBill.id}`, payload);
        toast.success('Bill updated');
      } else {
        await axios.post(`${API}/bills`, payload);
        toast.success('Bill created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bill');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/bills/${id}`);
      toast.success('Bill deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete bill');
    }
  };

  const getPreviewPaymentDate = () => {
    if (!formData.bill_date || !formData.supplier_id) return null;
    const supplier = suppliers.find(s => s.id === formData.supplier_id);
    if (!supplier) return null;
    const totalDays = (supplier.terms_days || 0) + (parseInt(formData.extra_days) || 0);
    const billDate = parseISO(formData.bill_date);
    const paymentDate = addDays(billDate, totalDays);
    return {
      date: format(paymentDate, 'dd MMM yyyy'),
      month: format(paymentDate, 'MMM yyyy'),
      totalDays
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(value);
  };

  // ---------- Filtering & Selection ----------
  const filteredBills = bills.filter((b) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matches = (b.supplier_name || '').toLowerCase().includes(q)
        || (b.description || '').toLowerCase().includes(q)
        || String(b.amount || '').includes(q);
      if (!matches) return false;
    }
    if (dateFrom || dateTo) {
      if (!b.expected_payment_date) return false;
      const due = parseISO(b.expected_payment_date);
      if (dateFrom && due < startOfDay(parseISO(dateFrom))) return false;
      if (dateTo && due > endOfDay(parseISO(dateTo))) return false;
    }
    return true;
  });

  const filteredTotal = filteredBills.reduce((sum, b) => sum + (b.amount || 0), 0);
  const allFilteredSelected = filteredBills.length > 0
    && filteredBills.every((b) => selectedIds.has(b.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredBills.forEach((b) => next.delete(b.id));
      } else {
        filteredBills.forEach((b) => next.add(b.id));
      }
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
  };

  const handleBulkDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/bills/bulk-delete`, { ids });
      toast.success(`${res.data.deleted} bill${res.data.deleted === 1 ? '' : 's'} deleted`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bills');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeleteAllFiltered = async () => {
    const ids = filteredBills.map((b) => b.id);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/bills/bulk-delete`, { ids });
      toast.success(`${res.data.deleted} bill${res.data.deleted === 1 ? '' : 's'} deleted`);
      setSelectedIds(new Set());
      setDeleteAllStep(0);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bills');
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const preview = getPreviewPaymentDate();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bills (AP)
            </h1>
            <p className="text-slate-500 mt-1">
              Track accounts payable and expected payment timing
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-primary hover:bg-primary/90"
                onClick={() => handleOpenDialog()}
                disabled={suppliers.length === 0}
                data-testid="add-bill-btn"
              >
                <Plus className="w-4 h-4" />
                Add Bill
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingBill ? 'Edit Bill' : 'Add Bill'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bill_date">Bill Date</Label>
                    <Input
                      id="bill_date"
                      type="date"
                      value={formData.bill_date}
                      onChange={(e) => setFormData({ ...formData, bill_date: e.target.value })}
                      data-testid="bill-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplier">Supplier</Label>
                    <Select
                      value={formData.supplier_id}
                      onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
                    >
                      <SelectTrigger data-testid="bill-supplier-select">
                        <SelectValue placeholder="Select supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name} ({supplier.terms_days} days)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Bill description"
                    data-testid="bill-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Amount (AUD)</Label>
                    <Input
                      id="amount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="bill-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="extra_days">Extra Days</Label>
                    <div className="flex gap-2">
                      <Input
                        id="extra_days"
                        type="number"
                        value={formData.extra_days}
                        onChange={(e) => setFormData({ ...formData, extra_days: e.target.value })}
                        placeholder="0"
                        className="flex-1"
                        data-testid="bill-extra-days-input"
                      />
                      {(() => {
                        const supplier = suppliers.find(s => s.id === formData.supplier_id);
                        const disabled = !formData.bill_date || !supplier;
                        const baseDays = supplier?.terms_days || 0;
                        const formalDate = formData.bill_date && supplier
                          ? addDays(parseISO(formData.bill_date), baseDays)
                          : null;
                        const currentPickedDate = formalDate
                          ? addDays(formalDate, parseInt(formData.extra_days, 10) || 0)
                          : null;
                        return (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                disabled={disabled}
                                title={disabled ? 'Select a bill date and supplier first' : 'Pick a specific due date'}
                                data-testid="bill-pick-date-btn"
                              >
                                <CalendarDays className="w-4 h-4" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 w-auto" align="end">
                              <Calendar
                                mode="single"
                                selected={currentPickedDate || undefined}
                                onSelect={(d) => {
                                  if (!d || !formalDate) return;
                                  const delta = differenceInDays(d, formalDate);
                                  setFormData({ ...formData, extra_days: delta });
                                }}
                                initialFocus
                              />
                              <div className="px-3 py-2 border-t border-slate-200 text-[11px] text-slate-500 bg-slate-50">
                                Picks the exact payment date — adjusts Extra Days automatically.
                              </div>
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                    </div>
                    <p className="text-xs text-slate-500">Type extra days or pick a specific due date</p>
                  </div>
                </div>

                {/* Payment Preview */}
                {preview && (
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                      <CalendarIcon className="w-4 h-4" />
                      <span className="font-medium">Expected Payment</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Bill Date</span>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                      <span className="font-mono text-sm text-red-600 font-medium">{preview.date}</span>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      {preview.totalDays} days total • Cash outflow in <span className="font-semibold text-slate-700">{preview.month}</span>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-bill-btn">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-bill-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingBill ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {suppliers.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800 text-sm">
              Please add suppliers first before creating bills.
            </p>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {bills.length === 0 ? (
              <div className="empty-state py-16">
                <Receipt className="empty-state-icon w-16 h-16 text-slate-300 mb-4" />
                <h3 className="empty-state-title text-lg font-semibold text-slate-900 mb-2">No bills yet</h3>
                <p className="empty-state-text text-sm text-slate-500 max-w-md">
                  Add bills to track expected outgoing payments and their impact on your cash flow.
                </p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <Input
                      type="search"
                      placeholder="Search by supplier, description, or amount..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="max-w-sm"
                      data-testid="bills-search-input"
                    />
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500 whitespace-nowrap">Due From</Label>
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="h-9 w-36"
                        data-testid="bills-date-from"
                      />
                      <Label className="text-xs text-slate-500 whitespace-nowrap">To</Label>
                      <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="h-9 w-36"
                        data-testid="bills-date-to"
                      />
                      {(dateFrom || dateTo) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearDateRange}
                          className="h-8 px-2 text-slate-500"
                          data-testid="bills-clear-range-btn"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                    <div
                      className="inline-flex items-center gap-3 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 dark:bg-slate-800 dark:border-slate-700"
                      data-testid="bills-summary-panel"
                    >
                      <span className="text-xs text-slate-500 uppercase tracking-wider">
                        {(dateFrom || dateTo) ? 'In Range' : 'All'}
                      </span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100" data-testid="bills-summary-count">
                        {filteredBills.length} bill{filteredBills.length === 1 ? '' : 's'}
                      </span>
                      <span className="text-slate-300">·</span>
                      <span className="text-sm font-mono font-semibold text-red-700" data-testid="bills-summary-total">
                        {formatCurrency(filteredTotal)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedIds.size > 0 && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              disabled={bulkDeleting}
                              data-testid="bills-delete-selected-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-1.5" />
                              Delete Selected ({selectedIds.size})
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {selectedIds.size} bill{selectedIds.size === 1 ? '' : 's'}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the selected bills. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleBulkDeleteSelected}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="bills-confirm-delete-selected"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-700 border-red-200 hover:bg-red-50"
                        disabled={filteredBills.length === 0 || bulkDeleting}
                        onClick={() => setDeleteAllStep(1)}
                        data-testid="bills-delete-all-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Delete All{(dateFrom || dateTo || searchQuery) ? ' Filtered' : ''} ({filteredBills.length})
                      </Button>
                    </div>
                  </div>
                </div>
              <div className="overflow-x-auto">
                <Table data-testid="bills-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allFilteredSelected}
                          onCheckedChange={toggleSelectAll}
                          disabled={filteredBills.length === 0}
                          aria-label="Select all"
                          data-testid="bills-select-all"
                        />
                      </TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Supplier</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Amount</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-center">Terms + Extra</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Expected Payment</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBills.map((bill) => (
                      <TableRow key={bill.id} data-testid={`bill-row-${bill.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(bill.id)}
                            onCheckedChange={() => toggleRow(bill.id)}
                            aria-label="Select row"
                            data-testid={`bills-select-${bill.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {format(parseISO(bill.bill_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">{bill.supplier_name}</TableCell>
                        <TableCell className="text-slate-600 max-w-xs truncate">{bill.description || '-'}</TableCell>
                        <TableCell className="text-right font-mono text-sm cash-negative">
                          {formatCurrency(bill.amount)}
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm">
                          {bill.terms_days} + {bill.extra_days} = {bill.total_days}d
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-mono text-sm">{format(parseISO(bill.expected_payment_date), 'dd MMM yyyy')}</span>
                            <span className="text-xs text-slate-500">{format(parseISO(bill.payment_month), 'MMM yyyy')}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(bill)}
                              data-testid={`edit-bill-${bill.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-bill-${bill.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Bill</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this bill? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(bill.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete-All double confirmation */}
      <AlertDialog
        open={deleteAllStep === 1}
        onOpenChange={(open) => { if (!open) setDeleteAllStep(0); }}
      >
        <AlertDialogContent data-testid="bills-delete-all-step1">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {filteredBills.length} bill{filteredBills.length === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {(dateFrom || dateTo || searchQuery) ? 'all currently filtered bills' : 'every bill'} ({formatCurrency(filteredTotal)} total)?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bills-delete-all-step1-cancel">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); setDeleteAllStep(2); }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="bills-delete-all-step1-yes"
            >
              Yes, continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteAllStep === 2}
        onOpenChange={(open) => { if (!open) setDeleteAllStep(0); }}
      >
        <AlertDialogContent data-testid="bills-delete-all-step2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">This cannot be undone</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete {filteredBills.length} bill{filteredBills.length === 1 ? '' : 's'} ({formatCurrency(filteredTotal)} total).
              This action is irreversible. Are you absolutely sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bills-delete-all-step2-cancel">No, keep them</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllFiltered}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="bills-delete-all-step2-yes"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Yes, delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
